import { Button, Textarea, useToast } from '@memorizecards/ui';
import type { ReactNode } from 'react';
import { useState } from 'react';
import { formatDateTime } from '../lib/format';
import {
  useApagarComentario,
  useComentarios,
  usePostarComentario,
  useVotarComentario,
} from '../lib/queries';
import { Carregando, Erro } from './estado';

const MAX_CHARS = 2000;

export function Comentarios({ cardId }: { cardId: number }): ReactNode {
  const { data, isPending, error, refetch } = useComentarios(cardId);
  const postar = usePostarComentario(cardId);
  const votar = useVotarComentario(cardId);
  const apagar = useApagarComentario(cardId);
  const toast = useToast();

  const [texto, setTexto] = useState('');

  async function enviar() {
    const limpo = texto.trim();
    if (!limpo) return;
    try {
      await postar.mutateAsync({ texto: limpo });
      setTexto('');
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Não consegui enviar.');
    }
  }

  return (
    <div className="flex flex-col gap-5">
      <div className="flex flex-col gap-3">
        <Textarea
          label="Comentar este card"
          value={texto}
          maxLength={MAX_CHARS}
          onChange={(e) => setTexto(e.target.value)}
          placeholder="Uma dica, um macete, uma correção…"
          className="min-h-24"
        />
        <div className="flex items-center justify-between gap-3">
          <span className="mc-mono">
            {texto.length}/{MAX_CHARS}
          </span>
          <Button
            size="sm"
            onClick={() => void enviar()}
            loading={postar.isPending}
            disabled={texto.trim().length === 0}
          >
            Publicar
          </Button>
        </div>
      </div>

      {isPending ? <Carregando /> : null}
      {error ? <Erro error={error} aoTentarNovamente={() => void refetch()} /> : null}

      {data && data.length === 0 ? (
        <p className="py-4 text-center text-sm text-mc-ink3">
          Nenhum comentário ainda. Seja o primeiro.
        </p>
      ) : null}

      <ul className="flex flex-col divide-y divide-mc-line">
        {(data ?? []).map((comentario) => (
          <li key={comentario.id} className="flex gap-4 py-4">
            <div className="flex shrink-0 flex-col items-center gap-1">
              <VotoBotao
                rotulo="Concordo"
                ativo={comentario.meuVoto === 1}
                onClick={() =>
                  votar.mutate({ commentId: comentario.id, valor: 1 })
                }
              >
                <svg viewBox="0 0 16 16" className="size-3.5 fill-current">
                  <path d="M8 2l6 8H2z" />
                </svg>
              </VotoBotao>
              <span
                className={`text-sm font-semibold tabular-nums ${
                  comentario.saldo > 0
                    ? 'text-mc-accent'
                    : comentario.saldo < 0
                      ? 'text-mc-danger'
                      : 'text-mc-ink3'
                }`}
              >
                {comentario.saldo}
              </span>
              <VotoBotao
                rotulo="Discordo"
                ativo={comentario.meuVoto === -1}
                onClick={() =>
                  votar.mutate({ commentId: comentario.id, valor: -1 })
                }
              >
                <svg viewBox="0 0 16 16" className="size-3.5 fill-current">
                  <path d="M8 14L2 6h12z" />
                </svg>
              </VotoBotao>
            </div>

            <div className="min-w-0 flex-1">
              <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
                <span className="text-sm font-medium text-mc-ink">
                  {comentario.usuario}
                </span>
                <span className="mc-mono">{formatDateTime(comentario.data)}</span>
                {comentario.meu ? (
                  <button
                    type="button"
                    onClick={() => apagar.mutate(comentario.id)}
                    className="mc-mono text-mc-danger underline underline-offset-2"
                  >
                    apagar
                  </button>
                ) : null}
              </div>
              <p className="mt-1.5 whitespace-pre-wrap break-words text-sm text-mc-ink2">
                {comentario.texto}
              </p>
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}

function VotoBotao({
  rotulo,
  ativo,
  onClick,
  children,
}: {
  rotulo: string;
  ativo: boolean;
  onClick: () => void;
  children: ReactNode;
}): ReactNode {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={rotulo}
      aria-pressed={ativo}
      className={`grid size-8 place-items-center rounded-full border transition-colors ${
        ativo
          ? 'border-mc-accent text-mc-accent'
          : 'border-mc-line2 text-mc-ink3 hover:border-mc-line2 hover:text-mc-ink'
      }`}
    >
      {children}
    </button>
  );
}
